<template>
  <div class="video-pane">
    <b-button class="video-button"
              v-if="videoAvailable"
              :disabled="!videoAvailable"
              @click="togglePane">
      <b-icon-person-square/>
    </b-button>
    <b-sidebar v-if="videoAvailable"
               right
               id="video-sidebar"
               class="bg-light"
               body-class="overflow-auto"
               :title="$i18n.gt('HEADING_VIDEO')"
               :no-close-on-route-change="true"
               :visible="paneVisible">
      <template v-slot:title>
        Telemedicine
      </template>
      <div class="px-0 py-2 sidebar-content">
        <!-- enable/disable -->
        <div class="text-center mb-3">
          <b-form-checkbox v-model="videoEnabled" switch @change="enableVideo">
            {{ videoAvailableLabel }}
          </b-form-checkbox>
        </div>
        <!-- video components -->
        <template v-if="videoEnabled">
          <!-- remote video -->
          <h3 class="ml-3">{{ remoteVideo }}</h3>
          <div class="position-relative w-100 bg-secondary">
            <video ref="remoteVideo" class="w-100" autoplay playsinline controls/>
            <div v-if="!is_connected"
                 class="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                 style="bottom:0;background:rgba(0,0,0,0.6);">
              <b-icon-x-lg variant="warning" font-scale="4"/>
            </div>
          </div>
          <!-- local video -->
          <div class="mt-3 ml-3 d-flex justify-content-between">
            <h3>{{ localVideo }}</h3>
            <div class="d-flex justify-content-around">
              <!-- microphone/mute -->
              <b-input-group class="w-auto pr-2">
                <b-button variant="transparent" size="sm" class="pl-3 pr-1" @click="toggleDevice('mic')">
                  <b-icon :icon="mic ? 'mic-fill' : 'mic-mute-fill'"
                          :variant="mic ? 'dark' : 'danger'"
                          font-scale="1.5"/>
                </b-button>
                <b-dropdown no-caret variant="transparent" size="sm" toggle-class="p-1" menu-class="overflow-visible">
                  <template #button-content>
                    <b-icon-caret-down-fill variant="dark"/>
                  </template>
                  <b-dropdown-item v-for="(device,i) in micDevices"
                                   :variant="(device.value === micDevice) ? 'primary' : ''"
                                   :key="`mic${i}`"
                                   @click="setDevice('mic', device.value)">
                    <b-icon-check v-if="device.value === micDevice"/>
                    <b-icon-blank v-else/>
                    {{ device.text }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-input-group>
              <!-- camera/hide -->
              <b-input-group class="w-auto pr-2">
                <b-button variant="transparent" size="sm" class="pl-3 pr-1" @click="toggleDevice('camera')">
                  <b-icon :icon="camera ? 'camera-video-fill' : 'camera-video-off-fill'"
                          :variant="camera ? 'dark' : 'danger'"
                          font-scale="1.5"/>
                </b-button>
                <b-dropdown no-caret variant="transparent" size="sm" toggle-class="p-1" menu-class="overflow-visible">
                  <template #button-content>
                    <b-icon-caret-down-fill variant="dark"/>
                  </template>
                  <b-dropdown-item v-for="(device,i) in cameraDevices"
                                   :variant="(device.value === cameraDevice) ? 'primary' : ''"
                                   :key="`camera${i}`"
                                   @click="setDevice('camera', device.value)">
                    <b-icon-check v-if="device.value === cameraDevice"/>
                    <b-icon-blank v-else/>
                    {{ device.text }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-input-group>
              <!-- speaker -->
              <!--
              <b-input-group>
                <b-button variant="light" class="pl-3 pr-1" @click="toggleDevice('speaker')">
                  <b-icon :icon="speaker ? 'volume-down-fill' : 'volume-mute-fill'"
                          :variant="speaker ? '' : 'danger'"
                          font-scale="2"/>
                </b-button>
                <b-dropdown no-caret variant="light" size="sm" toggle-class="p-1" menu-class="overflow-visible">
                  <template #button-content>
                    <b-icon-caret-down-fill variant="dark"/>
                  </template>
                  <b-dropdown-item v-for="(device,i) in speakerDevices"
                                   :variant="(device.value === speakerDevice) ? 'primary' : ''"
                                   :key="`speaker${i}`"
                                   @click="setDevice('speaker', device.value)">
                    <b-icon-check v-if="device.value === speakerDevice"/>
                    <b-icon-blank v-else/>
                    {{ device.text }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-input-group>
              -->
            </div>
          </div>
          <div class="position-relative w-100 bg-secondary clear-both">
            <video ref="localVideo" class="w-100" autoplay playsinline controls/>
            <div v-if="!camera"
                 class="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                 style="bottom:0;background:rgba(0,0,0,0.6);">
              <b-icon-camera-video-off-fill variant="danger" font-scale="4"/>
            </div>
          </div>
          <div class="mt-2 mb-3 d-flex justify-content-around">
            <b-button v-if="videoState === IDLE" class="" @click="startVideo(MASTER)">
              Host Meeting
            </b-button>
            <b-button v-if="videoState === IDLE" class="" @click="startVideo(VIEWER)">
              Join Meeting
            </b-button>
            <b-button v-if="videoState !== IDLE" class="" @click="stopVideo">
              Leave Meeting
            </b-button>
          </div>
        </template>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import $ from 'jquery'
import Utils from '@/providers/Utils'
import AWS from 'aws-sdk'
import { SignalingClient } from 'amazon-kinesis-video-streams-webrtc'

/**
 * constants
 */

/**
 * Renders a video button on the page, which hosts video sessions
 */
export default Vue.extend({
  name: 'VideoPane',
  data () {
    return {
      // constants
      IDLE: null,
      MASTER: 'MASTER',
      VIEWER: 'VIEWER',
      // credentials - TODO: SECURE THIS LATER
      //   https://stackoverflow.com/questions/61920185/how-to-use-kinesis-video-stream-webrtc-sdk-in-the-browser-without-providing-cred
      //   https://github.com/awslabs/amazon-kinesis-video-streams-webrtc-sdk-js/blob/master/README.md#class-signalingclient
      AWS_ACCESS_KEY: 'AKIAQAE7EO72VPOE3S2A',
      AWS_SECRET_KEY: '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',
      AWS_REGION: 'us-east-1',
      AWS_CHANNEL: 'shs-webrtc-test',
      AWS_SIGNALING_ARN: 'arn:aws:kinesisvideo:us-east-1:000333740021:channel/shs-webrtc-test/1728487791730',
      AWS_CLIENT_ID: 'test1',
      // video
      videoEnabled: this.$store.getters.get('videoEnabled') || false,
      videoState: this.$store.getters.get('videoState') || null,
      mic: this.$store.getters.get('mic') || true,
      micDevice: this.$store.getters.get('micDevice') || null,
      camera: this.$store.getters.get('camera') || true,
      cameraDevice: this.$store.getters.get('cameraDevice') || null,
      // speaker: this.$store.getters.get('speaker') || true,
      // speakerDevice: this.$store.getters.get('speakerDevice') || null,
      // devices
      micDevices: [],
      cameraDevices: [],
      //speakerDevices: [],
      // video connection
      kinesisVideo: null,
      signalingClient: null,
      localStream: null,
      peerConnection: null,
      connectionState: null,
    }
  },
  computed: {
    videoAvailable () {
      return this.$user.userId()
    },
    videoAvailableLabel () {
      return this.$i18n.gt(this.videoAvailable ? 'LABEL_VIDEO_ENABLE' : 'LABEL_VIDEO_DISABLE')
    },
    is_connected () {
      return (this.videoState !== this.IDLE) && (this.connectionState === 'connected')
    },
    remoteVideo () {
      return this.$i18n.gt('VIDEO_REMOTE')
    },
    localVideo () {
      return this.$i18n.gt('VIDEO_LOCAL')
    },
    paneVisible: {
      get () {
        return this.$store.state.videoPane
      },
      set (val) {
        // save & persist
        this.$store.commit('set',
          val
            ? { videoPane: true, helpPane: false, chatPane: false }
            : { videoPane: false })
        // persist
        this.$store.dispatch('save')
      }
    },
  },
  methods: {
    save () {
      const payload = {
        videoEnabled: this.videoEnabled,
        videoState: this.videoState,
        mic: this.mic,
        micDevice: this.micDevice,
        camera: this.camera,
        cameraDevice: this.cameraDevice,
        //speaker: this.speaker,
        //speakerDevice: this.speakerDevice,
      }
      this.$store.commit('set', payload)
      // persist
      this.$store.dispatch('save')
    },
    enableVideo () {
      console.log('VideoPane:enableVideo  this.videoEnabled=', this.videoEnabled)
      //this.videoEnabled = !this.videoEnabled
      if (this.videoEnabled)
        this.initVideo()
      else
        this.stopVideo()
      this.save()
    },
    initVideo () {
      // DEBUG
      this.videoState = this.IDLE
      // get default audio/video devices
      const constraints = { video: true, audio: true }
      navigator.mediaDevices.getUserMedia(constraints)
        .then(stream => {
          // persist with window
          window.stream = stream
          // refresh button list in case labels have become available
          return navigator.mediaDevices.enumerateDevices()
        })
        .then(devices => {
          this.micDevices = []
          this.cameraDevices = []
          //this.speakerDevices = []
          // add new selections
          devices.forEach(device => {
            let value = device.deviceId
            let text = device.label || ''
            if (device.kind === 'audioinput') {
              this.micDevices.push({
                value: value,
                text: text.length ? text : `microphone ${this.micDevices.length + 1}`
              })
              if (!this.micDevice)
                this.micDevice = value
            } else if (device.kind === 'videoinput') {
              this.cameraDevices.push({
                value: value,
                text: text.length ? text : `camera ${this.cameraDevices.length + 1}`
              })
              if (!this.cameraDevice)
                this.cameraDevice = value
            } else if (device.kind === 'audiooutput') {
              //   this.speakerDevices.push({
              //     value: value,
              //     text: text.length ? text : `speaker ${this.speakerDevices.length + 1}`
              //   })
              //   if (!this.speakerDevice)
              //     this.speakerDevice = value
            } else
              console.error('VideoPane:initVideo  unknown device=', device)
          })
          // check selection
          if (this.micDevice && !_.find(this.micDevices, { value: this.micDevice }))
            this.micDevice = this.micDevices.length ? this.micDevices[0].value : null
          if (this.cameraDevice && !_.find(this.cameraDevices, { value: this.cameraDevice }))
            this.cameraDevice = this.cameraDevices.length ? this.cameraDevices[0].value : null
          //if (this.speakerDevice && !_.find(this.speakerDevices, { value: this.speakerDevice }))
          //  this.speakerDevice = this.speakerDevices.length ? this.speakerDevices[0].value : null
          // persist
          this.save()
          // set source
          this.setSource()
        })
        .catch(error => {
          console.error('VideoPane:initVideo  error=', error.message, error.name)
        })
    },
    togglePane () {
      this.paneVisible = !this.paneVisible
    },
    async setSource () {
      console.log('VideoPane:setSource')
      // get new stream
      const constraints = {
        audio: { deviceId: this.mic ? { exact: this.micDevice } : false },
        video: { deviceId: this.camera ? { exact: this.cameraDevice } : false }
      }
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints)
      if (this.$refs.localVideo)
        this.$refs.localVideo.srcObject = this.localStream
      // set mute/hide
      this.setMuteHide()
      // possibly replace active peerConnection tracks
      if (this.peerConnection) {
        const senders = this.peerConnection.getSenders()
        const localTracks = this.localStream ? this.localStream.getTracks() : []
        if (senders)
          senders.forEach(sender => {
            if (sender.track) {
              const kind = sender.track.kind
              const localTrack = _.find(localTracks, { kind: kind })
              if (!localTrack)
                this.peerConnection.removeTrack(sender)
              else if (!_.find(localTracks, { id:sender.track.id}))
                sender.replaceTrack(localTrack)
            }
          })
      }
    },
    setMuteHide () {
      // local tracks
      let localTracks = this.localStream ? this.localStream.getTracks() : []
      if (localTracks)
        localTracks.forEach(localTrack => {
          if (localTrack.kind === 'audio')
            localTrack.enabled = this.mic
          else if (localTrack.kind === 'video')
            localTrack.enabled = this.camera
        })
    },
    async toggleDevice (type) {
      // update mic/camera
      this[type] = !this[type]
      this.save()
      // set mute/hide
      this.setMuteHide()
    },
    async setDevice (type, value) {
      this[type + 'Device'] = value
      this.save()
      if ((type === 'mic') || (type === 'camera'))
        this.setSource()
      // else if (type === 'speaker') {
      //   console.log('VideoPane:toggleDevice  value=', value)
      //   if (value) {
      //     if (this.$refs.remoteVideo)
      //       this.$refs.remoteVideo.setSinkId(value)
      //         .then(() => {
      //           console.log('VideoPane:setDevice  remoteVideo  type=', type, '  value=', value)
      //         })
      //         .catch(error => {
      //           console.error('VideoPane:setDevice  error=', error)
      //         })
      //     if (this.$refs.localVideo)
      //       this.$refs.localVideo.setSinkId(value)
      //         .then(() => {
      //           console.log('VideoPane:setDevice  localVideo  type=', type, '  value=', value)
      //         })
      //         .catch(error => {
      //           console.error('VideoPane:setDevice  error=', error)
      //         })
      //   }
      // }
    },
    async startVideo (role) {
      console.log('VideoPane:startVideo  role=', role)
      if ((role !== 'MASTER') && (role !== 'VIEWER'))
        console.error('VidepPane:startVideo  role must be MASTER or VIEWER  role=', role)
      // set state
      this.videoState = role
      this.save()
      // clear remote
      if (this.$refs.remoteVideo)
        this.$refs.remoteVideo.srcObject = null
      // trap errors
      try {
        // channel ARN
        const describeChannelResponse = await this.kinesisVideo.describeSignalingChannel({
          ChannelName: this.AWS_CHANNEL,
        }).promise()
        const channelARN = describeChannelResponse.ChannelInfo.ChannelARN
        // endpoints
        const endpointsResponse = await this.kinesisVideo.getSignalingChannelEndpoint({
          ChannelARN: channelARN,
          SingleMasterChannelEndpointConfiguration: {
            Protocols: ['WSS', 'HTTPS'],
            Role: role
          }
        }).promise()
        const endpointsByProtocol = endpointsResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
          endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint
          return endpoints
        }, {})
        //console.log('VideoPane:startVideo  endpointsByProtocol=', endpointsByProtocol)
        // signaling client
        this.signalingClient = new SignalingClient({
          channelARN: channelARN,
          channelEndpoint: endpointsByProtocol.WSS,
          role: role,
          region: this.AWS_REGION,
          credentials: AWS.config.credentials,
          clientId: (role === this.MASTER) ? null : this.AWS_CLIENT_ID
        })
        let iceServers = [
          { urls: `stun:stun.kinesisvideo.${this.AWS_REGION}.amazonaws.com:443` }
        ]
        const kinesisVideoSignalingChannelsClient = new AWS.KinesisVideoSignalingChannels({
          region: this.AWS_REGION,
          credentials: AWS.config.credentials,
          endpoint: endpointsByProtocol.HTTPS,
          correctClockSkew: true,
        })
        const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient.getIceServerConfig({
          ChannelARN: channelARN,
        }).promise()
        getIceServerConfigResponse.IceServerList.forEach(iceServer =>
          iceServers.push({
            urls: iceServer.Uris,
            username: iceServer.Username,
            credential: iceServer.Password,
          }),
        )
        // peer connection
        this.peerConnection = new RTCPeerConnection({
          iceServers: iceServers
        })
        this.peerConnection.onicecandidate = ({ candidate }) => {
          if (candidate)    // otherwise all ICE candidates have been generated
            this.signalingClient.sendIceCandidate(candidate)
        }
        this.peerConnection.ontrack = event => {
          console.log('VideoPane:peerConnection:ontrack  event.streams=', event ? event.streams : null)
          if (event.streams && event.streams[0] && this.$refs.remoteVideo) {
            if (!this.$refs.remoteVideo.srcObject)    // issue with changing source camera/audio?
              this.$refs.remoteVideo.srcObject = event.streams[0]
            else if (this.$refs.remoteVideo.srcObject !== event.streams[0]) {
              console.log('VideoPane:peerConnection:ontrack  remote srcObject already set  this.$refs.remoteVideo.srcObject=', this.$refs.remoteVideo.srcObject, '  event.streams[0]=', event.streams[0])
              this.$refs.remoteVideo.srcObject = event.streams[0]
            }
          }
        }
        this.peerConnection.onconnectionstatechange = event => {
          this.connectionState = this.peerConnection.connectionState
          //  new, connecting, connected, disconnected, failed, closed
          console.log('VideoPane:peerConnection:onconnectionstatechange  this.connectionState=', this.connectionState)
          if (this.connectionState === 'failed') {
            this.stopVideo()
            Vue.toasted.error('Telemedicine disconnected', { duration: 5000 })
          }
        }
        // this.peerConnection.ondatachannel = event => {
        //   event.channel.onmessage = onRemoteDataMessage
        // }
        // signaling client event handlers
        this.signalingClient.on('open', async () => {
          console.log('VideoPane:signalingClient:open')
          // set local source
          await this.setSource()    // don't forget to wait
          // add tracks
          if (this.peerConnection) {
            let localTracks = this.localStream ? this.localStream.getTracks() : []
            if (localTracks) {
              localTracks.forEach(localTrack => {
                console.log('VideoPane:signalingClient:open  adding  localTrack.id=', localTrack.id, '  localTrack.kind=', localTrack.kind)
                this.peerConnection.addTrack(localTrack, this.localStream)
              })
            }
          }
          // create and send offer
          const offer = await this.peerConnection.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
          })
          await this.peerConnection.setLocalDescription(offer)
          this.signalingClient.sendSdpOffer(this.peerConnection.localDescription)
        })
        this.signalingClient.on('sdpAnswer', async (answer) => {
          console.log('VideoPane:signalingClient:sdpAnswer  answer=', answer)
          if (this.peerConnection)
            this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))
        })
        this.signalingClient.on('sdpOffer', async (offer, remoteClientId) => {
          console.log('VideoPane:signalingClient:sdpOffer  offer=', offer)
          if (this.peerConnection) {
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer))
            if (role === this.MASTER) {
              await this.peerConnection.setRemoteDescription(offer)
              //await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer))
              const answer = await this.peerConnection.createAnswer()
              await this.peerConnection.setLocalDescription(answer)
              this.signalingClient.sendSdpAnswer(this.peerConnection.localDescription, remoteClientId)
            } else {
              await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer))
              const answer = await this.peerConnection.createAnswer()
              await this.peerConnection.setLocalDescription(answer)
              this.signalingClient.sendSdpAnswer(this.peerConnection.localDescription)
            }
          }
        })
        // when an ICE candidate is received from the master, add it to the peer connection.
        this.signalingClient.on('iceCandidate', async (candidate) => {
          //console.log('VideoPane:signalingClient:iceCandidate  candidate=', candidate)
          if (candidate && this.peerConnection)
            await this.peerConnection.addIceCandidate(candidate)
        })
        //
        this.signalingClient.on('close', () => {
          console.log('VideoPane:signalingClient:close')
        })
        this.signalingClient.on('error', error => {
          console.error('VideoPane:signalingClient:error  error=', error)
          Vue.toasted.error(
            'Telemedicine error: ' + (error ? error.message : error),
            { duration: 5000 }
          )
        })
        // open the signaling client
        this.signalingClient.open()
      } catch (error) {
        console.error('VideoPane:signalingClient:startVideo  error=', error)
        Vue.toasted.error(
          'Telemedicine error: ' + (error ? error.message : error),
          { duration: 5000 }
        )
        this.stopVideo()
      }
    },
    async stopVideo () {
      if (this.videoState !== this.IDLE) {
        // set state
        this.videoState = this.IDLE
        this.connectionState = null
        // trap errors
        try {
          // deactivate remote video
          if (this.$refs.remoteVideo)
            this.$refs.remoteVideo.srcObject = null
          // stop signalingClient
          if (this.signalingClient) {
            await this.signalingClient.close()
            this.signalingClient = null
          }
          // close peer connection
          if (this.peerConnection) {
            await this.peerConnection.close()
            // clear peerConnection callbacks
            this.peerConnection.onicecandidate = null
            this.peerConnection.ontrack = null
            this.peerConnection.onconnectionstatechange = null
            this.peerConnection = null
          }
        } catch (error) {
          console.error('VideoPane:stopVideo  error=', error)
        }
        // persist video state
        this.save()
      }
    },
  },
  created () {
    // AWS configuration
    AWS.config.update({
      region: this.AWS_REGION,
      accessKeyId: this.AWS_ACCESS_KEY,
      secretAccessKey: this.AWS_SECRET_KEY
    })
    // initialize kinesis video
    this.kinesisVideo = new AWS.KinesisVideo({
      region: this.AWS_REGION,
      correctClockSkew: true,
    })
  },
  mounted () {
    console.log('VideoPane:mounted  videoAvailable=', this.videoAvailable, '  videoEnabled=', this.videoEnabled, '  videoPane=', this.$store.state.videoPane)
    if (this.videoAvailable && this.videoEnabled)
      this.initVideo()
  }
})
</script>

<style lang="scss">
.video-pane {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 2;

  header.b-sidebar-header > .close {
    height: 32px;
    width: 32px;
  }

  .b-sidebar {
    .b-sidebar-body {
      overflow-y: visible;
    }
  }

  #video-sidebar {
    box-shadow: 0 0 20px rgba(0, 0, 0, .1);
    border-left: solid 1px var(--border-color);
    background-color: var(--accent-color-lighten-40-opaque) !important;
    height: calc(100vh - 44px);
  }

  .video-button {
    background: var(--base-color) !important;
    color: #fff !important;
    margin: 0 5px;
    border-radius: 5px 5px 0 0;
    display: inline-block;

    position: absolute;
    right: 106px;
    bottom: 0;
    padding: 7px;

    &:active, &:focus {
      background-color: var(base-color);
    }

    svg {
      font-size: 30px !important;
    }
  }

  button.close {
    border: solid 1px var(--blue-lighten-30);
    background-color: #fff;
    padding: 3px;
    border-radius: 50%;
    cursor: pointer;
    opacity: 1;

    svg {
      color: var(--blue-lighten-30);
    }

    &:hover {
      background-color: var(--btn-bg-hover);
      border-color: var(--btn-bg-hover);
      opacity: 1 !important;

      svg {
        color: #fff !important;
      }
    }
  }
}
</style>
